import dayjs from "dayjs";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";

import authContext from "@context/Auth/authContext";
import LocationContext from "@context/location/locationContext";
import { Box } from "@mui/material";

import LoaderContext from "@context/Loader/loaderContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import { message } from "@src/constants";
import Cookies from "js-cookie";
import { checkAndUpdateDate } from "@src/utils/checkAndUpdateDate";
import {
  ContentWrapper,
  CustomCardHeading,
  CustomCardSubHeading,
  CustomLoginButton,
  CustomLoginNote,
  LoginPageCardWrapper,
} from "./style";

const LoginPageCard = () => {
  const navigate = useNavigate();

  const [{ setState }] = useContext(authContext);
  const [{ locationState, locationSetState }] = useContext(LocationContext);
  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);

  const [{ loaderState, loaderSetState }] = useContext(LoaderContext);

  const handleLoginAction = async () => {
    const url = `${process.env.REACT_APP_BASEURL_API}/auth`;

    const newWindow = window.open(url, "_blank", "width=500,height=600");
    let timer: ReturnType<typeof setTimeout> | null = null;
    loaderSetState({
      ...loaderState,
      loginLoader: true,
    });

    if (newWindow) {
      timer = setInterval(() => {
        // stop loading
        const handleStopLoading = () => {
          loaderSetState({
            ...loaderState,
            loginLoader: false,
          });
          if (timer) clearInterval(timer);
        };

        if (newWindow?.closed) {
          const userError = Cookies.get("userError") || "";

          if (userError) {
            showErrorToastSetState({
              errorMessage: message.ERROR_MESSAGE,
              errorTitle: userError,
              open: true,
            });

            Cookies.remove("userError");
            handleStopLoading();
            return;
          }

          const userData = Cookies.get("userData") || "";
          const parsedUserData = userData ? JSON.parse(userData) : null;

          if (!parsedUserData) {
            showErrorToastSetState({
              errorMessage: "",
              errorTitle: message.ERROR_MESSAGE,
              open: true,
            });

            handleStopLoading();
            return;
          }

          const recentState = {
            token: parsedUserData?.access_token,
            email: parsedUserData?.user?.email,
            level: parsedUserData?.user?.level,
            name: parsedUserData?.user?.name,
            user_id: parsedUserData?.user?.user_id,
          };

          if (!recentState.level) {
            showErrorToastSetState({
              errorMessage: "Level information not found!",
              errorTitle: "Couldn't fetch authorization details!",
              open: true,
            });

            handleStopLoading();
            return;
          }

          if (!recentState.token) {
            showErrorToastSetState({
              errorMessage: message.ERROR_MESSAGE,
              errorTitle: message.ERROR_MESSAGE_UNAUTHORIZED,
              open: true,
            });

            handleStopLoading();
            return;
          }

          // for local storage
          const initialLocationLocalIds = {
            property: parsedUserData?.locationDetails?.property_id || "",
            store_id: parsedUserData?.locationDetails?.store_id || "",
            rvc_id: parsedUserData?.locationDetails?.rvc_id || "",
          };

          // for context
          const initialLocationIds = {
            property: parsedUserData?.locationDetails?.property_id || "",
            store: parsedUserData?.locationDetails?.store_id || "",
            revenueCenter: parsedUserData?.locationDetails?.rvc_id || "",
          };

          const initialLocationDetails = {
            store: parsedUserData?.locationDetails?.store_name || "",
            revenueCenter: parsedUserData?.locationDetails?.rvc_name || "",
            property: parsedUserData?.locationDetails?.property_name || "",
            propertyShortname:
              parsedUserData?.locationDetails?.property_short_name || "",
          };

          localStorage.setItem(
            "locationDetails",
            JSON.stringify(initialLocationDetails)
          );

          localStorage.setItem(
            "locationIds",
            JSON.stringify(initialLocationLocalIds)
          );

          const currentDate = checkAndUpdateDate();
          const initialDate = dayjs(currentDate).format("YYYY-MM-DD");

          locationSetState({
            ...locationState,
            ...{ ...initialLocationIds, date: initialDate },
          });

          setState(recentState);

          localStorage.setItem("date", JSON.stringify(initialDate));

          if (parsedUserData?.access_token) navigate("/");

          handleStopLoading();
        }
      }, 500);
    }
  };

  return (
    <LoginPageCardWrapper>
      <Box>
        <ContentWrapper height="40">
          <CustomCardHeading>Welcome!</CustomCardHeading>
        </ContentWrapper>
      </Box>
      <Box sx={{ marginTop: "16px" }}>
        <ContentWrapper height="48">
          <CustomCardSubHeading>
            This dashboard requires users to log in <br />
            using their official @mlse.com account.
          </CustomCardSubHeading>
        </ContentWrapper>
      </Box>
      <CustomLoginButton onClick={handleLoginAction}>
        Login with MLSE ID
      </CustomLoginButton>
      <Box
        sx={{
          alignItems: "center",
        }}
      >
        <ContentWrapper height="40">
          <CustomLoginNote>
            <strong>Note:</strong> You will be briefly redirected to another
            website for log-in.
          </CustomLoginNote>
        </ContentWrapper>
      </Box>
    </LoginPageCardWrapper>
  );
};

export default LoginPageCard;
